import { Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link as RouterLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar, Box, Chip, Divider, Drawer, IconButton, InputAdornment, List, ListItemButton, ListItemIcon,
  ListItemText, Menu, MenuItem, TextField, Toolbar, Tooltip, Typography, useMediaQuery, useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LogoutIcon from "@mui/icons-material/Logout";
import DashboardIcon from "@mui/icons-material/SpaceDashboard";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BadgeIcon from "@mui/icons-material/Badge";
import GroupsIcon from "@mui/icons-material/Groups";
import ApartmentIcon from "@mui/icons-material/Apartment";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import WorkIcon from "@mui/icons-material/Work";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PlaceIcon from "@mui/icons-material/Place";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ExtensionIcon from "@mui/icons-material/Extension";
import PersonIcon from "@mui/icons-material/Person";
import TodayIcon from "@mui/icons-material/Today";
import RuleIcon from "@mui/icons-material/Rule";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import InboxIcon from "@mui/icons-material/Inbox";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import TuneIcon from "@mui/icons-material/Tune";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SettingsIcon from "@mui/icons-material/Settings";
import ApprovalIcon from "@mui/icons-material/Approval";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import KeyIcon from "@mui/icons-material/Key";
import AssessmentIcon from "@mui/icons-material/Assessment";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import SyncAltIcon from "@mui/icons-material/SyncAlt";

import { useAuth } from "@/store/auth";
import { useSession } from "@/store/session";
import { AppearanceMenu } from "@/components/AppearanceMenu";
import { BrandLogo } from "@/components/BrandMark";
import { NotificationBell } from "@/components/NotificationBell";
import { InstallPrompt } from "@/components/InstallPrompt";
import { ChangePasswordDialog } from "@/components/PasswordChangeGate";
import { DetailSkeleton } from "@/components/Skeletons";

const SIDEBAR_OPEN = 264;
const SIDEBAR_RAIL = 72;

/**
 * The desktop sidebar has three modes, cycled by the menu button in the app bar — the same cycle
 * HospitalERP uses, so staff moving between the two apps find the same control in the same place:
 * the full menu, an icon-only rail, and hidden. A phone always gets the full menu in a drawer.
 */
type SidebarMode = "open" | "rail" | "hidden";
const SIDEBAR_MODE_KEY = "hms_sidebar_mode";

/** Browser storage can be blocked (private windows, strict policies); the menu must still render. */
function readSidebarMode(): SidebarMode {
  try {
    const stored = window.localStorage.getItem(SIDEBAR_MODE_KEY);
    return stored === "rail" || stored === "hidden" ? stored : "open";
  } catch {
    return "open";
  }
}

const NEXT_MODE: Record<SidebarMode, SidebarMode> = { open: "rail", rail: "hidden", hidden: "open" };
const MENU_BUTTON_LABEL: Record<SidebarMode, string> = {
  open: "Show icons only",
  rail: "Hide menu",
  hidden: "Show full menu",
};

interface NavItem {
  label: string;
  path: string;
  icon: ReactNode;
  /** Hidden unless the user holds this permission. */
  permission?: string;
  /** Hidden unless the user holds at least one of these — for screens staff and supervisors share. */
  anyPermission?: string[];
  /** Only for a login linked to a staff record — "my" screens have nobody to show otherwise. */
  requiresStaffLink?: boolean;
  /** Hidden unless the tenant has this feature on. */
  feature?: string;
}

interface NavGroup {
  key: string;
  title: string;
  items: NavItem[];
}

/**
 * The sidebar.
 *
 * <p>Every entry declares the permission and feature it needs, and is hidden when either is absent.
 * <b>Hiding is courtesy, not security</b> — the API re-checks both on every request — but a menu
 * full of things that 403 is its own kind of broken, so the two lists are kept honest against each
 * other by using the same string on both sides.</p>
 *
 * <p>Groups are declared here rather than assembled from the module catalog: the catalog says which
 * modules exist, and this says how their screens are arranged, which is a design decision. A group
 * with no visible items disappears, so an unlicensed module leaves no empty heading.</p>
 */
const NAV: NavGroup[] = [
  {
    key: "main",
    title: "",
    items: [
      { label: "Dashboard", path: "/dashboard", icon: <DashboardIcon />, permission: "duty.view" },
      { label: "Reports", path: "/reports", icon: <AssessmentIcon />, permission: "workforce.reports.view", feature: "reports.workforce" },
    ],
  },
  {
    key: "mine",
    title: "My work",
    items: [
      { label: "Today", path: "/my/today", icon: <TodayOutlinedIcon />, permission: "selfservice.view", feature: "selfservice.portal", requiresStaffLink: true },
      { label: "My roster", path: "/my/roster", icon: <CalendarViewMonthIcon />, permission: "selfservice.view", feature: "selfservice.portal", requiresStaffLink: true },
      { label: "My attendance", path: "/my/attendance", icon: <AssignmentTurnedInIcon />, permission: "selfservice.view", feature: "selfservice.portal", requiresStaffLink: true },
    ],
  },
  {
    key: "roster",
    title: "Roster",
    items: [
      { label: "Rosters", path: "/roster", icon: <CalendarMonthIcon />, permission: "roster.view", feature: "roster.core" },
      { label: "On duty today", path: "/roster/day", icon: <TodayIcon />, permission: "duty.view", feature: "roster.core" },
      { label: "Shifts", path: "/workforce/shifts", icon: <AccessTimeIcon />, permission: "shift.view", feature: "roster.core" },
      { label: "Duty types", path: "/workforce/duty-types", icon: <AssignmentIndIcon />, permission: "duty.view", feature: "roster.core" },
      { label: "Holiday calendars", path: "/workforce/holiday-calendars", icon: <EventAvailableIcon />, permission: "workforce.calendar.view", feature: "roster.core" },
      { label: "Weekly-off patterns", path: "/workforce/weekly-off-patterns", icon: <EventBusyIcon />, permission: "workforce.calendar.view", feature: "roster.core" },
      { label: "Roster rules", path: "/roster/rules", icon: <RuleIcon />, permission: "rules.manage", feature: "roster.core" },
      { label: "Staffing requirements", path: "/roster/staffing", icon: <GroupAddIcon />, permission: "roster.view", feature: "roster.core" },
    ],
  },
  {
    key: "workflow",
    title: "Workflow",
    items: [
      { label: "Approvals", path: "/workflow/approvals", icon: <ApprovalIcon />, anyPermission: ["roster.approve", "roster.publish", "swap.approve"], feature: "roster.core" },
      { label: "Duty swaps", path: "/workflow/swaps", icon: <SwapHorizIcon />, anyPermission: ["swap.request", "swap.approve"], feature: "workflow.swap" },
      { label: "Shift changes", path: "/workflow/shift-changes", icon: <ScheduleIcon />, anyPermission: ["shiftchange.request", "shiftchange.approve"], feature: "workflow.shiftchange" },
    ],
  },
  {
    key: "leave",
    title: "Leave",
    items: [
      { label: "Leave", path: "/leave", icon: <BeachAccessIcon />, anyPermission: ["leave.request", "leave.approve", "leave.view", "leave.manage"], feature: "leave.core" },
      { label: "Entitlements", path: "/leave/entitlements", icon: <EventRepeatIcon />, permission: "leave.view", feature: "leave.core" },
    ],
  },
  {
    key: "theatre",
    title: "Theatre",
    items: [
      { label: "Theatre board", path: "/theatre", icon: <MedicalServicesIcon />, permission: "theatre.view", feature: "theatre.assignment" },
      { label: "OT schedule", path: "/theatre/schedule", icon: <EventNoteIcon />, permission: "theatre.view", feature: "theatre.assignment" },
      { label: "Theatre setup", path: "/theatre/setup", icon: <SettingsIcon />, permission: "theatre.view", feature: "theatre.assignment" },
    ],
  },
  {
    key: "attendance",
    title: "Attendance",
    items: [
      { label: "Attendance", path: "/attendance", icon: <FactCheckIcon />, permission: "attendance.view", feature: "attendance.core" },
      { label: "Mark attendance", path: "/attendance/mark", icon: <HowToRegIcon />, permission: "attendance.mark", feature: "attendance.core" },
      { label: "Queues", path: "/attendance/queues", icon: <InboxIcon />, permission: "attendance.view", feature: "attendance.core" },
      { label: "Devices", path: "/attendance/devices", icon: <FingerprintIcon />, permission: "attendance.devices.manage", feature: "attendance.core" },
      { label: "Attendance policy", path: "/attendance/policy", icon: <TuneIcon />, permission: "attendance.view", feature: "attendance.core" },
    ],
  },
  {
    key: "people",
    title: "People",
    items: [
      { label: "Staff", path: "/master/employees", icon: <BadgeIcon />, permission: "workforce.employee.view", feature: "workforce.core" },
      { label: "Staff categories", path: "/workforce/staff-categories", icon: <GroupsIcon />, permission: "workforce.category.view", feature: "workforce.core" },
      { label: "Skills", path: "/workforce/skills", icon: <PsychologyIcon />, permission: "workforce.category.view", feature: "workforce.core" },
      { label: "Employment types", path: "/workforce/employment-types", icon: <WorkIcon />, permission: "workforce.category.view", feature: "workforce.core" },
      { label: "Leave types", path: "/workforce/leave-types", icon: <EventBusyIcon />, permission: "leave.view", feature: "leave.core" },
    ],
  },
  {
    key: "organisation",
    title: "Organisation",
    items: [
      { label: "Departments", path: "/master/departments", icon: <ApartmentIcon />, permission: "platform.masterdata.view" },
      { label: "Units", path: "/master/units", icon: <MeetingRoomIcon />, permission: "platform.masterdata.view" },
      { label: "Designations", path: "/master/designations", icon: <AssignmentIndIcon />, permission: "platform.masterdata.view" },
      { label: "Locations", path: "/master/locations", icon: <PlaceIcon />, permission: "platform.masterdata.view" },
    ],
  },
  {
    key: "admin",
    title: "Administration",
    items: [
      { label: "Feature flags", path: "/admin/feature-flags", icon: <ToggleOnIcon />, permission: "platform.admin.view" },
      { label: "Modules", path: "/admin/modules", icon: <ExtensionIcon />, permission: "platform.admin.view" },
      { label: "Users", path: "/admin/users", icon: <ManageAccountsIcon />, permission: "platform.user.view" },
      { label: "Roles", path: "/admin/roles", icon: <AdminPanelSettingsIcon />, permission: "platform.role.view" },
      // Not behind the integration flag: "paired but switched off" is one of the things it shows.
      { label: "HealNTrack", path: "/admin/healntrack", icon: <SyncAltIcon />, permission: "platform.integration.view" },
    ],
  },
];

/**
 * Menu search. An item matches on its own label or on its section's title, so "roster" finds every
 * roster screen, not only the one called "Rosters".
 */
function searchNav(groups: NavGroup[], query: string): NavGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return groups;

  return groups
    .map((group) =>
      group.title.toLowerCase().includes(q)
        ? group
        : { ...group, items: group.items.filter((item) => item.label.toLowerCase().includes(q)) },
    )
    .filter((group) => group.items.length > 0);
}

export function Layout() {
  const theme = useTheme();
  const shell = theme.shell;
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>(readSidebarMode);
  const [menuSearch, setMenuSearch] = useState("");
  const [userMenu, setUserMenu] = useState<null | HTMLElement>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  const searchInput = useRef<HTMLInputElement>(null);
  /** Set by the rail's search button: focus the field once the full menu has rendered it. */
  const focusSearchWhenOpen = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();

  const user = useAuth((s) => s.user);
  const clear = useAuth((s) => s.clear);
  const has = useAuth((s) => s.has);
  const featureOn = useSession((s) => s.featureOn);
  const isIntegrated = useSession((s) => s.isIntegrated);
  const features = useSession((s) => s.features);

  // The three modes are a desktop choice. A phone always gets the full menu in a temporary drawer.
  const mode: SidebarMode = isDesktop ? sidebarMode : "open";
  const showLabels = mode === "open";
  const drawerWidth = mode === "rail" ? SIDEBAR_RAIL : SIDEBAR_OPEN;
  /** How much of the page the sidebar takes: nothing on a phone, where the drawer floats over it. */
  const sidebarSpace = !isDesktop || mode === "hidden" ? 0 : drawerWidth;
  /** The wordmark lives in the full sidebar; when that is not showing, the app bar carries it. */
  const logoInAppBar = !isDesktop || mode !== "open";
  // The rail has no field to type into, so a search left behind must not keep filtering its icons.
  const query = showLabels ? menuSearch : "";

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_MODE_KEY, sidebarMode);
    } catch {
      // Not remembered across visits; the menu still works for this one.
    }
  }, [sidebarMode]);

  useEffect(() => {
    if (showLabels && focusSearchWhenOpen.current) {
      focusSearchWhenOpen.current = false;
      searchInput.current?.focus();
    }
  }, [showLabels]);

  const groups = useMemo(
    () =>
      NAV.map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            (!item.permission || has(item.permission)) &&
            (!item.anyPermission || item.anyPermission.some((p) => has(p))) &&
            (!item.requiresStaffLink || Boolean(user?.employeeId)) &&
            (!item.feature || featureOn(item.feature)),
        ),
      })).filter((group) => group.items.length > 0),
    // `features` is in the dependency list rather than `featureOn`: the function identity is stable
    // across store updates, so depending on it alone would freeze the menu at its first render.
    [has, featureOn, features, user],
  );

  const visibleGroups = useMemo(() => searchNav(groups, query), [groups, query]);

  /**
   * The one nav entry to highlight: the longest path that prefixes the current URL.
   *
   * <p>A plain prefix test lights up two entries at once as soon as one page nests under another —
   * `/roster/day` matches both "Rosters" and "On duty today" — and the reader cannot tell where
   * they are. Longest match picks the specific one.</p>
   */
  const activePath = useMemo(() => {
    const paths = groups.flatMap((group) => group.items.map((item) => item.path));

    return paths
      .filter((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))
      .sort((a, b) => b.length - a.length)[0];
  }, [groups, location.pathname]);

  const signOut = () => {
    clear();
    navigate("/login", { replace: true });
  };

  const onMenuButton = () => {
    if (isDesktop) setSidebarMode((current) => NEXT_MODE[current]);
    else setMobileOpen(true);
  };

  const onNavigated = () => {
    setMobileOpen(false);
    setMenuSearch("");
  };

  const openSearchFromRail = () => {
    focusSearchWhenOpen.current = true;
    setSidebarMode("open");
  };

  const widthTransition = theme.transitions.create(["width", "margin"], {
    duration: theme.transitions.duration.shorter,
  });

  /** Shared by the nav entries and the collapse control, so both line up in either mode. */
  const railAware = {
    mx: 1,
    borderRadius: 1,
    minHeight: 40,
    px: showLabels ? 2 : 0,
    justifyContent: showLabels ? "flex-start" : "center",
  } as const;
  const iconSx = { minWidth: showLabels ? 36 : 0, color: "inherit", justifyContent: "center" } as const;

  const searchField = (
    <Box sx={{ px: 2, pt: 0.5, pb: 1 }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Search menu…"
        value={menuSearch}
        onChange={(e) => setMenuSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setMenuSearch("");
          if (e.key === "Enter") {
            const first = visibleGroups[0]?.items[0];
            if (first) {
              navigate(first.path);
              onNavigated();
            }
          }
        }}
        inputRef={searchInput}
        inputProps={{ "aria-label": "Search menu" }}
        // The sidebar stays dark in both modes. Left alone, typed text takes `text.primary` — near
        // black in light mode, invisible here — so every colour is pinned to the shell tokens.
        sx={{
          "& .MuiInputBase-root": { color: shell.brand },
          "& .MuiInputBase-input": {
            color: shell.brand,
            caretColor: shell.brand,
            "&::placeholder": { color: shell.text, opacity: 1 },
          },
          "& .MuiSvgIcon-root": { color: shell.text },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.18)" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: shell.text },
          "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: shell.activeText },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: menuSearch ? (
            <InputAdornment position="end">
              <IconButton size="small" edge="end" aria-label="Clear menu search" onClick={() => setMenuSearch("")}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
    </Box>
  );

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: shell.bg, color: shell.text }}>
      <Toolbar sx={{ px: showLabels ? 2 : 0, justifyContent: showLabels ? "flex-start" : "center", color: shell.brand }}>
        {showLabels ? (
          <BrandLogo />
        ) : (
          <Tooltip title="Search menu" placement="right">
            <IconButton onClick={openSearchFromRail} aria-label="Search menu" sx={{ color: shell.text }}>
              <SearchIcon />
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>

      {showLabels && searchField}

      <Box sx={{ overflowY: "auto", overflowX: "hidden", flex: 1, pb: 2 }}>
        {visibleGroups.map((group, index) => (
          <Box key={group.key}>
            {/* A rule between sections, so they stay apart in the rail, where there are no titles. */}
            {index > 0 && <Divider sx={{ borderColor: shell.border, mx: showLabels ? 2 : 1.5, my: 1 }} />}
            {showLabels && group.title && (
              // The sidebar text colour, dimmed — not `shell.border`, which is a 9% line colour and
              // left these headings invisible on the dark shell in both modes.
              <Typography
                variant="overline"
                sx={{
                  px: 2.5, pb: 0.5, display: "block", lineHeight: 2,
                  color: shell.text, opacity: 0.7, fontWeight: 700, letterSpacing: ".08em",
                }}
              >
                {group.title}
              </Typography>
            )}
            <List dense disablePadding>
              {group.items.map((item) => {
                const selected = item.path === activePath;
                return (
                  <Tooltip key={item.path} title={showLabels ? "" : item.label} placement="right">
                    <ListItemButton
                      component={RouterLink}
                      to={item.path}
                      selected={selected}
                      onClick={onNavigated}
                      aria-label={showLabels ? undefined : item.label}
                      sx={{
                        ...railAware,
                        color: selected ? shell.activeText : shell.text,
                        bgcolor: selected ? shell.activeBg : "transparent",
                        "&:hover": { bgcolor: shell.hover },
                        "&.Mui-selected:hover": { bgcolor: shell.activeBg },
                      }}
                    >
                      <ListItemIcon sx={iconSx}>{item.icon}</ListItemIcon>
                      {showLabels && <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />}
                    </ListItemButton>
                  </Tooltip>
                );
              })}
            </List>
          </Box>
        ))}

        {query.trim() && visibleGroups.length === 0 && (
          <Typography variant="body2" sx={{ px: 2.5, py: 2, color: shell.text, opacity: 0.7 }}>
            No menu items match “{query.trim()}”.
          </Typography>
        )}
      </Box>

      {isDesktop && (
        <Box sx={{ py: 1, borderTop: 1, borderColor: shell.border }}>
          <Tooltip title={showLabels ? "" : "Show full menu"} placement="right">
            <ListItemButton
              onClick={() => setSidebarMode(showLabels ? "rail" : "open")}
              aria-label={showLabels ? undefined : "Show full menu"}
              sx={{ ...railAware, color: shell.text, "&:hover": { bgcolor: shell.hover } }}
            >
              <ListItemIcon sx={iconSx}>{showLabels ? <ChevronLeftIcon /> : <ChevronRightIcon />}</ListItemIcon>
              {showLabels && <ListItemText primary="Collapse" primaryTypographyProps={{ fontSize: 13 }} />}
            </ListItemButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );

  const menuButtonLabel = isDesktop ? MENU_BUTTON_LABEL[sidebarMode] : "Open menu";

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        sx={{
          width: `calc(100% - ${sidebarSpace}px)`,
          ml: `${sidebarSpace}px`,
          transition: widthTransition,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <Tooltip title={menuButtonLabel}>
            <IconButton edge="start" onClick={onMenuButton} aria-label={menuButtonLabel}>
              <MenuIcon />
            </IconButton>
          </Tooltip>

          {logoInAppBar && (
            <Box sx={{ display: "flex", minWidth: 0 }}>
              <BrandLogo height={26} />
            </Box>
          )}

          <Box sx={{ flex: 1 }} />

          {/*
            The one place deployment mode surfaces in the UI. Worth showing rather than leaving a
            user to discover it from a refused save on a master screen.
          */}
          {isIntegrated && (
            <Tooltip title="Staff and organisation master data is maintained in HealNTrack for this hospital.">
              <Chip size="small" variant="outlined" color="info" label="HealNTrack linked" />
            </Tooltip>
          )}

          <InstallPrompt />

          {featureOn("notifications.core") && <NotificationBell />}

          <AppearanceMenu />

          <Tooltip title={user?.email ?? ""}>
            <IconButton onClick={(e) => setUserMenu(e.currentTarget)} size="small">
              <PersonIcon />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={userMenu} open={Boolean(userMenu)} onClose={() => setUserMenu(null)}>
            <MenuItem disabled sx={{ opacity: "1 !important" }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>{user?.fullName}</Typography>
                <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setUserMenu(null); setChangingPassword(true); }}>
              <ListItemIcon><KeyIcon fontSize="small" /></ListItemIcon>
              Change password
            </MenuItem>
            <MenuItem onClick={signOut}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
          {changingPassword && <ChangePasswordDialog onClose={() => setChangingPassword(false)} />}
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: sidebarSpace, flexShrink: 0, transition: widthTransition }}>
        {/* Hidden is not rendered at all, so its links cannot be reached by Tab while out of sight. */}
        {mode !== "hidden" && (
          <Drawer
            variant={isDesktop ? "permanent" : "temporary"}
            open={isDesktop || mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                boxSizing: "border-box",
                border: "none",
                overflowX: "hidden",
                transition: widthTransition,
              },
            }}
          >
            {drawer}
          </Drawer>
        )}
      </Box>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, bgcolor: "background.default" }}>
        <Toolbar />
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* Pages load on first visit; the shell stays put and the content area shows a skeleton. */}
          <Suspense fallback={<DetailSkeleton />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
}
